import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../Modal/Modal';
import { 
  Package, 
  ShoppingBag, 
  Zap, 
  CheckCircle2, 
  List, 
  Clock, 
  MessageCircle,
  Instagram,
  DollarSign,
  FileText,
  Settings
} from 'lucide-react';
import CONFIG from '../../config/config';
import { datadogRum } from '@datadog/browser-rum';

interface OutletModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const OutletModal: React.FC<OutletModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  const handleWhatsApp = () => {
    datadogRum.addAction('Outlet Modal - WhatsApp Clicked');
    window.open(CONFIG.CONTACT_INFO.WHATSAPP, '_blank', 'noopener,noreferrer');
  };

  const handleInstagram = () => {
    datadogRum.addAction('Outlet Modal - Instagram Clicked');
    window.open(CONFIG.CONTACT_INFO.INSTAGRAM, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    if (isOpen) {
      datadogRum.addAction('Outlet Modal - Opened');
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal 
      modalTitle="home.banner.outlet.modal.title" 
      canClose 
      onClose={onClose}
    >
      <div className="space-y-6">
        {/* Problem Statement Section */}
        <div className="bg-pink-50 rounded-lg p-6 border-l-4 border-pink-400">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="bg-pink-100 rounded-full p-3">
                <Package className="h-6 w-6 text-pink-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {t("home.banner.outlet.modal.problem.title", "We Know Your Challenge")}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {t("home.banner.outlet.modal.problem.text", "Getting rid of sample and unsold dresses is a common problem every year. Selling them gets harder because of physical space limits and new collections taking priority.")}
              </p>
            </div>
          </div>
        </div>

        {/* Solution Section */}
        <div className="bg-pink-50 rounded-lg p-6 border-l-4 border-pink-400">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <Zap className="h-6 w-6 text-pink-600" />
            <span>{t("home.banner.outlet.modal.solution.title", "Our Solution")}</span>
          </h3>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="flex-shrink-0 bg-pink-100 rounded-full p-2">
                  <DollarSign className="h-5 w-5 text-pink-600" />
                </div>
                <h4 className="font-semibold text-gray-900">
                  {t("home.banner.outlet.modal.solution.cost.title", "Fraction of the Cost")}
                </h4>
              </div>
              <p className="text-sm text-gray-700">
                {t("home.banner.outlet.modal.solution.cost.text", "We help you sell online at a fraction of the percentage of other solutions.")}
              </p>
            </div>

            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="flex-shrink-0 bg-pink-100 rounded-full p-2">
                  <CheckCircle2 className="h-5 w-5 text-pink-600" />
                </div>
                <h4 className="font-semibold text-gray-900">
                  {t("home.banner.outlet.modal.solution.setup.title", "We Set It All Up")}
                </h4>
              </div>
              <p className="text-sm text-gray-700">
                {t("home.banner.outlet.modal.solution.setup.text", "We handle the entire setup process for you.")}
              </p>
            </div>

            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="flex-shrink-0 bg-pink-100 rounded-full p-2">
                  <ShoppingBag className="h-5 w-5 text-pink-600" />
                </div>
                <h4 className="font-semibold text-gray-900">
                  {t("home.banner.outlet.modal.solution.marketplace.title", "Expanding Your Reach")}
                </h4>
              </div>
              <p className="text-sm text-gray-700">
                {t("home.banner.outlet.modal.solution.marketplace.text", "Soon we will open a new marketplace for brides and your dresses will be automatically available to a wider audience.")}
              </p>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="bg-pink-50 rounded-lg p-6 border-l-4 border-pink-400">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <List className="h-6 w-6 text-pink-600" />
            <span>{t("home.banner.outlet.modal.process.title", "How It Works")}</span>
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 bg-pink-100 rounded-full p-2">
                <FileText className="h-4 w-4 text-pink-600" />
              </div>
              <div className="flex-1">
                <p className="text-gray-800">
                  {t("home.banner.outlet.modal.process.step1", "You provide us with a simple list: dresses, sizes, and desired prices")}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 bg-pink-100 rounded-full p-2">
                <Clock className="h-4 w-4 text-pink-600" />
              </div>
              <div className="flex-1">
                <p className="text-gray-800">
                  {t("home.banner.outlet.modal.process.step2", "We get everything set up in eAteliers in a matter of days")}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 bg-pink-100 rounded-full p-2">
                <Settings className="h-4 w-4 text-pink-600" />
              </div>
              <div className="flex-1">
                <p className="text-gray-800">
                  {t("home.banner.outlet.modal.process.step3", "You have full control to update your inventory over time")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action Section */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-700 rounded-lg p-6 text-white">
          <h3 className="text-xl font-bold mb-4 text-center">
            {t("home.banner.outlet.modal.cta.title", "Ready to Get Started?")}
          </h3>
          
          <div className="text-center">
            <p className="text-sm mb-4 opacity-90">
              {t("home.banner.outlet.modal.cta.reach_out", "Reach out directly:")}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handleWhatsApp}
                className="flex items-center space-x-2 bg-white text-black px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                <MessageCircle className="h-5 w-5" />
                <span>{CONFIG.CONTACT_INFO.PHONE}</span>
              </button>
              
              <button
                onClick={handleInstagram}
                className="flex items-center space-x-2 bg-white text-black px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                <Instagram className="h-5 w-5" />
                <span>@eateliers</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default OutletModal;

